import {
  Box,
  Button,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import React, { useEffect } from "react";
import * as Yup from "yup";
import api from "../../axiosInstance";
import { toast } from "react-toastify";
import {
  BUTTON_STYLES,
  FORM_STYLES,
  MODAL_STYLES,
  TEXT_FIELD_STYLES,
} from "../../styles/componentStyles";

const BlogCategoryValidationSchema = Yup.object({
  name: Yup.string().trim().required("Category name is required"),
});

function BlogCategoryModal({ open, onClose, categoryEdit, onSuccess }) {
  const formik = useFormik({
    initialValues: {
      name: categoryEdit?.name || "",
    },
    validationSchema: BlogCategoryValidationSchema,
    enableReinitialize: true,

    onSubmit: async (values, { resetForm }) => {
      const payload = { name: values.name.trim() };
      try {
        let response;
        if (categoryEdit?.id) {
          response = await api.put(
            `/api/admin/management/blog-categories/${categoryEdit.id}`,
            payload,
          );
        } else {
          response = await api.post(
            "/api/admin/management/blog-categories",
            payload,
          );
        }
        toast.success(
          `Category ${categoryEdit?.id ? "updated" : "added"} successfully!`,
          { position: "top-right", autoClose: 2000 },
        );
        onSuccess(response.data);
        resetForm();
        onClose();
      } catch (error) {
        toast.error(
          error.response?.data?.errorMessage ||
            "Failed to submit. Please try again.",
          { autoClose: 3000 },
        );
        console.error("API Error:", error);
      }
    },
  });

  // This component instance stays mounted across opens/closes (only the
  // Modal's `open` prop toggles), and `enableReinitialize` only resyncs
  // formik's values when the *prop-derived* initialValues actually differ
  // from last time -- if the admin edits the name, clicks Cancel without
  // saving, then reopens Edit on that SAME unchanged category, the prop
  // looks identical to before, so nothing re-triggers and the stale
  // unsaved value lingers in formik's own state. Explicitly resetting on
  // every open (Add -> empty, Edit -> the real current saved name) fixes
  // this regardless of what changed or didn't.
  useEffect(() => {
    if (open) {
      formik.resetForm({ values: { name: categoryEdit?.name || "" } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, categoryEdit]);

  return (
    <Modal open={open} onClose={onClose} disableScrollLock>
      <Box
        sx={{ ...MODAL_STYLES.standard, width: { xs: 340, md: 420 } }}
        component="form"
        onSubmit={formik.handleSubmit}
      >
        <Typography variant="h5" component="h2" sx={{ mb: 3, fontWeight: 600 }}>
          {categoryEdit?.id ? "Update Blog Category" : "Add Blog Category"}
        </Typography>

        <Box sx={FORM_STYLES.container}>
          <TextField
            autoFocus
            label="Category Name"
            name="name"
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.name && Boolean(formik.errors.name)}
            helperText={formik.touched.name && formik.errors.name}
            {...TEXT_FIELD_STYLES.standard}
            required
          />
        </Box>

        <Box
          sx={FORM_STYLES.actions}
          style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}
        >
          <Button onClick={onClose} {...BUTTON_STYLES.secondary}>
            Cancel
          </Button>
          <Button type="submit" {...BUTTON_STYLES.primary}>
            Save
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}

export default BlogCategoryModal;
